import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  CreateProductExcelImportParams,
  ProductExcelImportsFilterParams,
  ProductExcelImportsListResponse,
  ProductExcelImportType
} from '@/types/product-excel-import'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum } from '@/enums'

// API functions
import {
  bulkDeleteProductExcelImports,
  createProductExcelImport,
  deleteProductExcelImport,
  downloadProductExcelImportTemplate,
  fetchProductExcelImport,
  fetchProductExcelImports,
  retryProductExcelImport
} from '@/lib/api'

type ProductExcelImportStateType = {
  data: ProductExcelImportType | null
  isLoading: boolean
  error: string | null
}

type ProductExcelImportsListStateType = {
  data: ProductExcelImportsListResponse | null
  isLoading: boolean
  error: string | null
}

type ProductExcelImportsStoreState = {
  productExcelImport: ProductExcelImportStateType
  productExcelImportsList: ProductExcelImportsListStateType
  filters: ProductExcelImportsFilterParams
  selectedProductExcelImportIds: string[]
  setFilters: (filters: Partial<ProductExcelImportsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchProductExcelImports: (params?: ProductExcelImportsFilterParams) => Promise<ProductExcelImportsListResponse>
  resetProductExcelImportsList: () => void

  // ProductExcelImport operations
  getProductExcelImport: (id: string) => Promise<ProductExcelImportType>
  createProductExcelImport: (params: CreateProductExcelImportParams) => Promise<ProductExcelImportType>
  deleteProductExcelImport: (id: string) => Promise<void>
  retryProductExcelImport: (id: string) => Promise<ProductExcelImportType>
  downloadProductExcelImportTemplate: () => Promise<{ blob: Blob; filename: string | null }>
  bulkDeleteProductExcelImports: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectProductExcelImport: (id: string) => void
  deselectProductExcelImport: (id: string) => void
  toggleProductExcelImportSelection: (id: string) => void
  selectAllProductExcelImports: (productExcelImports: ProductExcelImportType[]) => void
  deselectAllProductExcelImports: () => void
  isProductExcelImportSelected: (id: string) => boolean
  resetProductExcelImport: () => void
}

const defaultFilters: ProductExcelImportsFilterParams = {
  categories: undefined,
  statuses: undefined,
  sort: SortEnum.ASC,
  sortBy: 'translations.en-US.title',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const useProductExcelImportsStore = create<ProductExcelImportsStoreState>()(
  devtools((set, get) => ({
    productExcelImport: {
      data: null,
      isLoading: false,
      error: null
    },
    productExcelImportsList: {
      data: null,
      isLoading: false,
      error: null
    },
    selectedProductExcelImportIds: [],

    filters: defaultFilters,

    setFilters: filters => {
      set(state => ({
        filters: { ...state.filters, ...filters }
      }))
    },

    resetFilters: () => {
      set({ filters: defaultFilters })
    },

    updateUrlParams: searchParams => {
      const filters: Partial<ProductExcelImportsFilterParams> = {}

      for (const [key, value] of searchParams.entries()) {
        if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as ProductExcelImportsFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers' || key === 'categories' || key === 'statuses') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof ProductExcelImportsFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (
            (key === 'createdUsers' || key === 'updatedUsers' || key === 'categories' || key === 'statuses') &&
            Array.isArray(value)
          ) {
            if (value.length > 0) {
              params.set(key, value.join(','))
            }
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getProductExcelImport: async id => {
      set(state => ({
        productExcelImport: {
          ...state.productExcelImport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchProductExcelImport(id)

        set(state => ({
          productExcelImport: {
            ...state.productExcelImport,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelImport: {
            ...state.productExcelImport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createProductExcelImport: async params => {
      set(state => ({
        productExcelImport: {
          ...state.productExcelImport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createProductExcelImport(params)

        set(state => ({
          productExcelImport: {
            ...state.productExcelImport,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelImport: {
            ...state.productExcelImport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    retryProductExcelImport: async id => {
      set(state => ({
        productExcelImport: {
          ...state.productExcelImport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await retryProductExcelImport(id)

        set(state => ({
          productExcelImport: {
            ...state.productExcelImport,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelImport: {
            ...state.productExcelImport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteProductExcelImport: async id => {
      set(state => ({
        productExcelImport: {
          ...state.productExcelImport,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteProductExcelImport(id)

        set(state => ({
          productExcelImport: {
            ...state.productExcelImport,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelImport: {
            ...state.productExcelImport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    downloadProductExcelImportTemplate: async () => {
      try {
        return await downloadProductExcelImportTemplate()
      } catch (error) {
        getErrorMessage(error)
        throw error
      }
    },

    resetProductExcelImport: () => {
      set(state => ({
        productExcelImport: {
          ...state.productExcelImport,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectProductExcelImport: id => {
      set(state => {
        if (state.selectedProductExcelImportIds.includes(id)) {
          return state
        }
        return { selectedProductExcelImportIds: [...state.selectedProductExcelImportIds, id] }
      })
    },

    deselectProductExcelImport: id => {
      set(state => ({
        selectedProductExcelImportIds: state.selectedProductExcelImportIds.filter(
          productExcelImportId => productExcelImportId !== id
        )
      }))
    },

    toggleProductExcelImportSelection: id => {
      set(state => {
        const isSelected = state.selectedProductExcelImportIds.includes(id)
        if (isSelected) {
          return {
            selectedProductExcelImportIds: state.selectedProductExcelImportIds.filter(
              productExcelImportId => productExcelImportId !== id
            )
          }
        } else {
          return { selectedProductExcelImportIds: [...state.selectedProductExcelImportIds, id] }
        }
      })
    },

    selectAllProductExcelImports: productExcelImports => {
      set({ selectedProductExcelImportIds: productExcelImports.map(productExcelImport => productExcelImport.id) })
    },

    deselectAllProductExcelImports: () => {
      set({ selectedProductExcelImportIds: [] })
    },

    isProductExcelImportSelected: id => {
      return get().selectedProductExcelImportIds.includes(id)
    },

    bulkDeleteProductExcelImports: async ids => {
      set(state => ({
        productExcelImport: {
          ...state.productExcelImport,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteProductExcelImports({ ids })

        set(state => ({
          productExcelImport: {
            ...state.productExcelImport,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedProductExcelImportIds: state.selectedProductExcelImportIds.filter(
            id => !response.deletedItems.includes(id)
          )
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelImport: {
            ...state.productExcelImport,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchProductExcelImports: async (params?: ProductExcelImportsFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        productExcelImportsList: {
          ...state.productExcelImportsList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchProductExcelImports(filtersToUse)

        set(state => ({
          productExcelImportsList: {
            ...state.productExcelImportsList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productExcelImportsList: {
            ...state.productExcelImportsList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetProductExcelImportsList: () => {
      set(state => ({
        productExcelImportsList: {
          ...state.productExcelImportsList,
          data: null,
          error: null
        }
      }))
    }
  }))
)

export default useProductExcelImportsStore
